import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar, Clock, MapPin, Truck, Play, CheckCircle, ClipboardList } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { formatTimeFromDate } from '../utils/dateFormatting';

const MyJobs = () => {
  const { api, user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timesheetDialog, setTimesheetDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [notes, setNotes] = useState('');
  const [timeFormat, setTimeFormat] = useState('24h');

  const fetchJobs = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get('/my-jobs', { params: { date: dateStr } });
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    api.get(`/tenants/${user.client_id}`).then(res => {
      if (res.data?.time_format) setTimeFormat(res.data.time_format);
    }).catch(() => {});
  }, [selectedDate, api]);

  const handleStartJob = async (timesheet) => {
    try {
      if (timesheet?.id) {
        await api.post(`/timesheets/${timesheet.id}/start`);
      } else {
        // Create timesheet first
        const newTimesheet = await api.post('/timesheets', {
          job_id: selectedJob.job.id,
          operator_id: selectedJob.allocation.operator_id,
          allocation_id: selectedJob.allocation.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          status: 'pending'
        });
        await api.post(`/timesheets/${newTimesheet.data.id}/start`);
      }
      toast.success('Job started');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to start job');
    }
  };

  const handleCompleteJob = async () => {
    if (!selectedJob?.timesheet?.id) return;
    try {
      await api.post(`/timesheets/${selectedJob.timesheet.id}/complete`, null, {
        params: { notes }
      });
      toast.success('Job completed');
      setTimesheetDialog(false);
      setNotes('');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to complete job');
    }
  };

  const openCompleteDialog = (jobItem) => {
    setSelectedJob(jobItem);
    setNotes(jobItem.timesheet?.notes || '');
    setTimesheetDialog(true);
  };

  const getStatusBadge = (timesheet) => {
    if (!timesheet) return <Badge variant="secondary">Pending</Badge>;
    switch (timesheet.status) {
      case 'started':
        return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
      case 'completed':
        return <Badge className="status-available">Completed</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const navigateDate = (direction) => {
    setSelectedDate(prev => direction > 0 ? addDays(prev, 1) : subDays(prev, 1));
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="my-jobs-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">My Jobs</h1>
        <p className="text-muted-foreground">Your assigned work for the day</p>
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigateDate(-1)}>
          Previous
        </Button>
        <div className="flex items-center gap-2 min-w-[200px] justify-center">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <span className="font-heading font-semibold">
            {format(selectedDate, 'EEEE, MMM d')}
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigateDate(1)}>
          Next
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setSelectedDate(new Date())}
          className="ml-2"
        >
          Today
        </Button>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <Card className="card-industrial">
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No jobs assigned for this day</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {jobs.map((item) => (
            <Card 
              key={item.allocation.id} 
              className={`card-industrial border-l-4 ${
                item.timesheet?.status === 'completed' 
                  ? 'border-[#ffd108]' 
                  : item.timesheet?.status === 'started'
                  ? 'border-blue-500'
                  : 'border-muted'
              }`}
              data-testid={`my-job-card-${item.allocation.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg">
                      {item.customer?.company_name || 'Unknown Customer'}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="h-4 w-4" />
                      {item.site?.address || 'No address'}
                    </p>
                  </div>
                  {getStatusBadge(item.timesheet)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{item.equipment?.equipment_type || item.equipment?.name || 'No equipment'}</span>
                      {item.equipment?.asset_id && (
                        <span className="text-xs font-mono text-muted-foreground">
                          ({item.equipment.asset_id})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-mono text-sm">{item.allocation.start_date}</span>
                    </div>
                  </div>

                  {item.timesheet?.start_time && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          Started: <span className="font-mono">{formatTimeFromDate(new Date(item.timesheet.start_time), timeFormat)}</span>
                        </span>
                      </div>
                      {item.timesheet?.end_time && (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-[#ffd108]" />
                          <span className="text-sm">
                            Finished: <span className="font-mono">{formatTimeFromDate(new Date(item.timesheet.end_time), timeFormat)}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {item.job?.description && (
                  <div className="p-3 bg-muted rounded-sm mb-4">
                    <p className="label-overline mb-1">Job Description</p>
                    <p className="text-sm">{item.job.description}</p>
                  </div>
                )}

                {item.job?.notes && (
                  <div className="p-3 bg-muted/50 rounded-sm mb-4">
                    <p className="label-overline mb-1">Notes</p>
                    <p className="text-sm text-muted-foreground">{item.job.notes}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  {(!item.timesheet || item.timesheet.status === 'pending') && (
                    <Button 
                      className="btn-volt flex-1"
                      onClick={() => {
                        setSelectedJob(item);
                        handleStartJob(item.timesheet);
                      }}
                      data-testid={`start-job-btn-${item.allocation.id}`}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start Job
                    </Button>
                  )}
                  {item.timesheet?.status === 'started' && (
                    <Button 
                      className="bg-[#ffd108] text-black hover:bg-[#E5B93D] flex-1"
                      onClick={() => openCompleteDialog(item)}
                      data-testid={`complete-job-btn-${item.allocation.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Complete Job
                    </Button>
                  )}
                  {item.timesheet?.status === 'completed' && (
                    <div className="flex-1 text-center py-2 text-[#ffd108] font-medium flex items-center justify-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Job Completed
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Complete Job Dialog */}
      <Dialog open={timesheetDialog} onOpenChange={setTimesheetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Complete Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm">
                <span className="font-medium">{selectedJob?.customer?.company_name}</span>
                <br />
                <span className="text-muted-foreground">{selectedJob?.site?.address}</span>
              </p>
            </div>
            <div className="space-y-2">
              <label className="label-overline">Completion Notes (Optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes about the job..."
                rows={4}
                data-testid="completion-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimesheetDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-volt" 
              onClick={handleCompleteJob}
              data-testid="confirm-complete-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyJobs;
